import {
  Bitcoin,
  CheckCheck,
  FileText,
  GitBranch,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { useTranslation } from "@/common/providers/i18n-provider";
import { AppHeader } from "@/components/app-header";
import { AppFooter } from "@/components/app-footer";
import { FaqAccordion } from "@/components/accordion";
import { StartFreeLink } from "@/components/cta-buttons";
import { LocaleLink } from "@/components/locale-link";
import { PricePicker } from "@/components/live-prices/price-picker";
import { livePricesFaqs } from "@/pages/live-prices-content";
import "@/styles/live-prices.css";

const CLI_GUIDE =
  "https://github.com/bex-co/beancount-io/blob/main/cli/docs/USAGE.md#managed-price-includes";

const PROOF_ITEMS = [
  { icon: FileText, key: "setup" },
  { icon: GitBranch, key: "commits" },
  { icon: ShieldCheck, key: "control" },
];

export function LivePricesPage() {
  const { t, messages, locale } = useTranslation();

  return (
    <div className="lp-page">
      <AppHeader />
      <main id="main">
        <section
          className="lp-hero lp-container"
          aria-labelledby="live-prices-title"
        >
          <div className="lp-hero-copy">
            <p className="lp-product-name">
              <span aria-hidden="true">
                <RefreshCw size={16} />
              </span>
              {t("livePrices.hero.label")}
            </p>
            <h1 id="live-prices-title">{t("livePrices.hero.title")}</h1>
            <p className="lp-lead">{t("livePrices.hero.description")}</p>
            <div className="lp-actions">
              <StartFreeLink surface="live_prices" />
              <a className="lp-text-link" href="#how-it-works">
                {t("livePrices.hero.how")}
              </a>
            </div>
            <p className="lp-hero-note">{t("livePrices.hero.note")}</p>
          </div>
          <PricePicker />
        </section>

        <div className="lp-proof lp-container">
          {PROOF_ITEMS.map(({ icon: Icon, key }) => (
            <div key={key}>
              <Icon size={18} aria-hidden="true" />
              <span>{t(`livePrices.proof.${key}`)}</span>
            </div>
          ))}
        </div>

        <section className="lp-problem lp-container">
          <h2>{t("livePrices.problem.title")}</h2>
          <p>{t("livePrices.problem.body")}</p>
        </section>

        <section
          id="how-it-works"
          className="lp-setup"
          aria-labelledby="price-setup-title"
        >
          <div className="lp-container lp-setup-grid">
            <div>
              <h2 id="price-setup-title">{t("livePrices.setup.title")}</h2>
              <ol className="lp-steps">
                {["one", "two", "three"].map((step, index) => (
                  <li key={step}>
                    <span className="lp-step-number" aria-hidden="true">
                      {new Intl.NumberFormat(locale).format(index + 1)}
                    </span>
                    <div>
                      <h3>{t(`livePrices.setup.${step}.title`)}</h3>
                      <p>{t(`livePrices.setup.${step}.body`)}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <figure className="lp-feed">
              <div className="lp-feed-header">
                <FileText size={18} aria-hidden="true" />
                <span>{t("livePrices.setup.sample")}</span>
              </div>
              <pre dir="ltr">
                <code>
                  <span className="lp-feed-comment">; BTC-USD</span>
                  {"\n\n"}
                  <span>2026-09-17</span> <strong>price</strong>
                  {" BTC 92000.00 USD\n\n"}
                  <span>2026-09-18</span> <strong>price</strong>
                  {" BTC 94000.00 USD\n"}
                  <span className="lp-feed-metadata">
                    {
                      '  price-source: "example-provider"\n  price-kind: "reference-spot"\n  observed-at: "2026-09-18T12:00:00Z"\n  provisional: TRUE'
                    }
                  </span>
                </code>
              </pre>
              <figcaption>{t("livePrices.setup.sampleNote")}</figcaption>
            </figure>
          </div>
        </section>

        <section
          className="lp-control lp-container"
          aria-labelledby="price-control-title"
        >
          <div className="lp-control-heading">
            <ShieldCheck size={28} aria-hidden="true" />
            <h2 id="price-control-title">{t("livePrices.control.title")}</h2>
          </div>
          <div className="lp-guarantees">
            {["manual", "history", "outage", "limit"].map((item) => (
              <div key={item}>
                <h3>
                  <CheckCheck size={19} aria-hidden="true" />
                  {t(`livePrices.control.${item}.title`)}
                </h3>
                <p>{t(`livePrices.control.${item}.body`)}</p>
              </div>
            ))}
          </div>
        </section>

        <section
          className="lp-scope lp-container"
          aria-labelledby="price-source-title"
        >
          <div>
            <h2 id="price-source-title">{t("livePrices.scope.title")}</h2>
            <p>{t("livePrices.scope.body")}</p>
            <LocaleLink className="lp-text-link" to="/tracking">
              {t("livePrices.scope.link")}
            </LocaleLink>
          </div>
          <div className="lp-coverage">
            <p className="lp-coverage-label">{t("livePrices.scope.catalog")}</p>
            <p className="lp-pair">
              <Bitcoin aria-hidden="true" size={24} />
              <span>{t("livePrices.scope.pair")}</span>
              <code>BTC-USD</code>
            </p>
            <p>{t("livePrices.scope.support")}</p>
            <a className="lp-text-link" href="#price-picker">
              {t("livePrices.scope.finder")}
            </a>
            <p className="lp-compatibility">
              {t("livePrices.scope.compatibility")}
            </p>
          </div>
        </section>

        <section
          className="lp-faq lp-container"
          aria-labelledby="price-faq-title"
        >
          <div>
            <h2 id="price-faq-title">{t("livePrices.faq.title")}</h2>
            <a className="lp-text-link" href={CLI_GUIDE}>
              {t("livePrices.faq.cli")}
            </a>
          </div>
          <FaqAccordion items={livePricesFaqs(messages)} />
        </section>

        <section className="lp-cta" aria-labelledby="price-cta-title">
          <div className="lp-container">
            <h2 id="price-cta-title">{t("livePrices.cta.title")}</h2>
            <p>{t("livePrices.cta.body")}</p>
            <div className="lp-actions">
              <StartFreeLink surface="live_prices" />
              <a
                className="lp-text-link"
                href="https://beancount.github.io/docs/beancount_language_syntax/#prices"
              >
                {t("livePrices.cta.docs")}
              </a>
            </div>
          </div>
        </section>
      </main>
      <AppFooter />
    </div>
  );
}
